import { StorageKey } from './const'

type StorageType = 'localStorage' | 'sessionStorage'

const getStorage = (storageType: StorageType) =>
  storageType === 'localStorage' ? localStorage : sessionStorage

export class ClientStorage<T> {
  readonly key: StorageKey

  readonly storageType: StorageType

  public static getValue<T>(key: StorageKey, storageType: StorageType): T | null

  public static getValue<T>(
    key: StorageKey,
    storageType: StorageType,
    fallbackValue: T,
  ): T

  public static getValue<T>(
    key: StorageKey,
    storageType: StorageType = 'localStorage',
    fallbackValue?: T,
  ) {
    const value = getStorage(storageType).getItem(key)

    if (!value || value === 'null' || value === 'undefined') {
      return fallbackValue ?? null
    }

    try {
      return JSON.parse(value) as T
    } catch (_) {
      return fallbackValue ?? null
    }
  }

  public static setValue<T>(
    key: StorageKey,
    storageType: StorageType = 'localStorage',
    data: T,
  ) {
    try {
      getStorage(storageType).setItem(key, JSON.stringify(data))
      return true
    } catch (_) {
      return false
    }
  }

  public static removeValue(key: StorageKey, storageType: StorageType = 'localStorage') {
    return getStorage(storageType).removeItem(key)
  }

  constructor(key: StorageKey, storageType: StorageType = 'localStorage') {
    this.key = key
    this.storageType = storageType
  }

  getValue(): T | null

  getValue(fallbackValue: T): T

  getValue(fallbackValue?: T) {
    return fallbackValue
      ? ClientStorage.getValue<T>(this.key, this.storageType, fallbackValue)
      : ClientStorage.getValue<T>(this.key, this.storageType)
  }

  setValue(data: T) {
    return ClientStorage.setValue(this.key, this.storageType, data)
  }

  removeValue() {
    return ClientStorage.removeValue(this.key, this.storageType)
  }
}
